import { type CatalogProductCard } from '../../../../types/ai';
import { extractProductIdFromCardAssets } from '../actions/product-actions';
import type { ProductCardData } from '../types';

export function parseProductCards(text: string): { cards: ProductCardData[]; cleanedText: string } {
  const cards: ProductCardData[] = [];
  const cardRegex = /\[PRODUCT_CARD\]([\s\S]*?)\[\/PRODUCT_CARD\]/g;

  let match;
  while ((match = cardRegex.exec(text)) !== null) {
    const cardContent = match[1];
    const card: Partial<ProductCardData> = {};

    const imageMatch = cardContent.match(/image:\s*(.+)/i);
    const brandMatch = cardContent.match(/brand:\s*(.+)/i);
    const titleMatch = cardContent.match(/title:\s*(.+)/i);
    const sizesMatch = cardContent.match(/sizes:\s*(.+)/i);
    const priceMatch = cardContent.match(/price:\s*(.+)/i);
    const urlMatch = cardContent.match(/url:\s*(.+)/i);
    const idMatch = cardContent.match(/^id:\s*(.+)/im);
    const slotMatch = cardContent.match(/^slot:\s*(.+)/im);
    const colorMatch = cardContent.match(/^color:\s*(.+)/im);
    const materialMatch = cardContent.match(/^material:\s*(.+)/im);
    const tryOnMatch = cardContent.match(/^tryOn:\s*true\s*$/im);
    const tryOnFramingMatch = cardContent.match(/^tryOnFraming:\s*feet\s*$/im);
    const reasonMatch = cardContent.match(/^reason:\s*(.+)$/im);

    if (imageMatch) card.image = imageMatch[1].trim();
    if (brandMatch) card.brand = brandMatch[1].trim();
    if (titleMatch) card.title = titleMatch[1].trim();
    if (reasonMatch) card.reason = reasonMatch[1].trim();
    if (sizesMatch) card.sizes = sizesMatch[1].trim();
    if (priceMatch) card.price = priceMatch[1].trim();
    if (urlMatch) card.url = urlMatch[1].trim();
    if (idMatch) card.id = idMatch[1].trim();
    if (slotMatch) card.slot = slotMatch[1].trim();
    if (colorMatch) card.color = colorMatch[1].trim();
    if (materialMatch) card.material = materialMatch[1].trim();
    if (tryOnMatch) card.tryOn = true;
    if (tryOnFramingMatch) card.tryOnFraming = 'feet';

    if (card.price) {
      const saleMatch = card.price.match(
        /^(.*?)\s*\(ord\.\s*([\d\s.,]+?)(?:\s*[A-Za-z]{3})?\s*,\s*[−-]\s*(\d{1,3})%\)$/,
      );
      if (saleMatch) {
        const regular = Number(saleMatch[2].replace(/[^\d]/g, ''));
        const percent = Number(saleMatch[3]);
        if (Number.isFinite(regular) && regular > 0 && Number.isFinite(percent) && percent > 0) {
          card.price = saleMatch[1].trim();
          card.regularPrice = regular;
          card.discountPercent = percent;
        }
      }
    }

    if (card.image && card.brand && card.title && card.price && card.url) {
      const parsedCard = card as ProductCardData;
      cards.push({
        ...parsedCard,
        id: parsedCard.id || extractProductIdFromCardAssets(parsedCard) || undefined,
      });
    }
  }

  let cleanedText = text.replace(cardRegex, '');
  cleanedText = cleanedText.replace(/\[PRODUCT_CARD\][\s\S]*$/, '');
  cleanedText = cleanedText.replace(/\[PRODUCT[-_A-Z]*$/i, '');

  return { cards, cleanedText };
}

// mcp-catalog stavar procentfältet "percemtage" — båda stavningarna måste gälla.
function formatCardMaterials(materials: unknown): string {
  if (typeof materials === 'string') return materials.trim();
  if (!Array.isArray(materials) || materials.length === 0) return '';
  return materials
    .map((m: unknown) => {
      if (typeof m === 'string') return m.trim();
      const entry = (m ?? {}) as { name?: unknown; percentage?: unknown; percemtage?: unknown };
      const name = String(entry.name ?? '').trim();
      if (!name) return '';
      const pct = entry.percentage ?? entry.percemtage;
      return typeof pct === 'number' && pct > 0 ? `${name} ${pct}%` : name;
    })
    .filter(Boolean)
    .slice(0, 3)
    .join(', ');
}

export function mapStructuredProductCards(
  cards: CatalogProductCard[] | undefined,
): ProductCardData[] {
  if (!Array.isArray(cards) || cards.length === 0) return [];

  return cards
    .map((card): ProductCardData | null => {
      const sizes = Array.isArray(card.sizes)
        ? card.sizes
            .map((size) => {
              if (typeof size === 'string') return size.trim();
              return String(size?.label ?? size?.size ?? '').trim();
            })
            .filter(Boolean)
            .join(', ')
        : typeof card.sizes === 'string'
          ? card.sizes.trim()
          : undefined;

      const title = String(card.title ?? card.name ?? '').trim();
      const image = String(card.imageUrl ?? card.image ?? '').trim();
      const brand = String(card.brand ?? '').trim();
      const priceValue =
        typeof card.price === 'number'
          ? `${card.price} ${String(card.currency ?? '').trim()}`.trim()
          : String(card.price ?? '').trim();
      const url = String(card.url ?? '').trim();
      const id = String(card.id ?? card.productId ?? '').trim();
      const slot = String(card.slot ?? '').trim();
      const color = String(card.color ?? '').trim();
      const material = formatCardMaterials(card.materials ?? card.material);
      const tryOn = (card as { tryOn?: unknown }).tryOn === true;
      const feet = (card as { tryOnFraming?: unknown }).tryOnFraming === 'feet';

      if (!image || !brand || !title || !priceValue || !url) {
        return null;
      }

      const regularPrice =
        typeof (card as Record<string, unknown>).regularPrice === 'number'
          ? ((card as Record<string, unknown>).regularPrice as number)
          : undefined;
      const discountPercent =
        typeof (card as Record<string, unknown>).discountPercent === 'number'
          ? ((card as Record<string, unknown>).discountPercent as number)
          : undefined;

      return {
        image,
        brand,
        title,
        sizes,
        price: priceValue,
        url,
        id: id || extractProductIdFromCardAssets({ image, url }) || undefined,
        slot: slot || undefined,
        color: color || undefined,
        material: material || undefined,
        ...(tryOn ? { tryOn: true } : {}),
        ...(feet ? { tryOnFraming: 'feet' as const } : {}),
        ...(regularPrice && discountPercent ? { regularPrice, discountPercent } : {}),
      } satisfies ProductCardData;
    })
    .filter((card): card is ProductCardData => card !== null);
}

export function humanizeOutfitCategoryLabel(value: unknown, fallback?: unknown): string {
  if (typeof fallback === 'string' && fallback.trim()) return fallback.trim();
  const normalized = String(value ?? '').trim();
  if (!normalized) return 'Plagg';
  const cleaned = normalized.replace(/[_-]+/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
